import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { BarChart3, UserCircle, Users, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function StudentsStats() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/students/stats").then(({ data }) => setData(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  if (!data) return <div className="skel h-64" />;

  const maxYear = Math.max(...data.by_year.map((y) => y.count), 1);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <BarChart3 className="w-7 h-7 text-[#1F7A63]" /> Statistiques des élèves
        </h1>
        <p className="text-sm text-[#5C736A]">Répartition et données démographiques.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total" value={data.total} icon={Users} color="#1F7A63" />
        <StatCard label="Garçons" value={data.garcons} icon={UserCircle} color="#4a9078" />
        <StatCard label="Filles" value={data.filles} icon={UserCircle} color="#E6B800" />
        <StatCard label="Âge moyen" value={data.age_moyen != null ? `${data.age_moyen} ans` : "—"} icon={BarChart3} color="#5C736A" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Répartition par sexe */}
        <div className="ek-card p-5">
          <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Répartition par sexe</h3>
          <div className="space-y-3">
            <BarRow label="Garçons" value={data.garcons} total={data.total} color="#4a9078" />
            <BarRow label="Filles" value={data.filles} total={data.total} color="#E6B800" />
          </div>
        </div>

        {/* Informations manquantes */}
        <div className="ek-card p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <AlertCircle className="w-5 h-5 text-red-500" /> Informations manquantes
          </h3>
          <div className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{data.with_missing_info}</div>
          <div className="text-sm text-[#5C736A] mt-1">
            élève(s) avec des informations manquantes (date de naissance, tuteur ou téléphone parent).
          </div>
        </div>
      </div>

      {/* Répartition par année */}
      <div className="ek-card p-5">
        <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Répartition par année de naissance</h3>
        {data.by_year.length === 0 ? (
          <div className="text-sm text-[#5C736A] py-4">Aucune donnée de date de naissance.</div>
        ) : (
          <div className="space-y-2">
            {data.by_year.map((y) => (
              <div key={y.year} className="flex items-center gap-3">
                <div className="w-16 text-sm font-medium">{y.year}</div>
                <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden relative">
                  <div className="bg-[#1F7A63] h-full transition-all" style={{ width: `${(y.count / maxYear) * 100}%` }} />
                  <div className="absolute inset-0 flex items-center px-3 text-xs font-semibold text-white mix-blend-difference">
                    {y.count} élève{y.count > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="ek-card p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-[#5C736A]">{label}</div>
          <div className="text-2xl sm:text-3xl font-bold mt-1" style={{ fontFamily: "Outfit", color }}>{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

function BarRow({ label, value, total, color }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium">{label}</span>
        <span className="text-[#5C736A]">{value} <span className="text-xs">({pct.toFixed(0)}%)</span></span>
      </div>
      <div className="bg-slate-100 rounded-full h-3 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
